<template>
  <div v-if="shouldShow" class="popup-wrap register-bonus-wrap" :class="{ active: shouldShow }">
    <div class="popup-box register-bonus-popup-box">
      <a class="popup-close" href="javascript:;" @click="close"><i class="fa-solid fa-xmark" /></a>

      <div class="form-popup popup-padding">
        <div class="rb-badge">
          <i class="fa-solid fa-gift" aria-hidden="true" />
        </div>

        <!-- 標題 -->
        <div class="title-block rb-title-block">
          <h4 class="verify-popup-title rb-title">
            {{ $t('Bao.register_bonus_title') }}
          </h4>
        </div>

        <!-- 金額 -->
        <div class="rb-amount">
          <span class="rb-amount-cur">{{ currencySymbol }}</span>
          <span class="rb-amount-num">{{ amount | numberFormat }}</span>
        </div>
        <p class="rb-amount-label">
          {{ $t('Bao.register_bonus_label') }}
        </p>

        <!-- 說明 -->
        <p class="rb-desc">
          {{ $t('Bao.register_bonus_desc', { amount: formattedAmount }) }}
        </p>

        <!-- 領取按鈕 -->
        <div class="form-button rb-btn-wrap">
          <button class="btn-primary rb-claim-btn" :disabled="claiming" @click="claim">
            <span>{{ $t('Bao.register_bonus_claim_btn') }}</span>
          </button>
        </div>

        <!-- 利用規約 -->
        <p class="rb-terms">
          {{ $t('Bao.register_bonus_terms') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { exchangeRegisterCoupon, info } from '@/api/user'
import { showNotice } from '@/utils/tool'

export default {
  name: 'RegisterBonusModal',
  data() {
    return {
      claiming: false,
      // 玩家按關閉後本次登入不再重跳（has_exchanged 仍為 0，換帳號會重置）
      dismissed: false,
      cssLoaded: false
    }
  },
  computed: {
    ...mapGetters(['token', 'memberInfo', 'brand', 'brandInfo', 'permissionInfo', 'app']),
    enableRegisterCoupon() {
      return Number(this.permissionInfo && this.permissionInfo.enable_register_coupon) === 1
    },
    registerCoupon() {
      return (this.memberInfo && this.memberInfo.register_coupon) || null
    },
    // 0 = 未領（可跳窗）、1 = 已領（不跳窗）；無資料時視為已領以免誤跳
    hasExchanged() {
      return this.memberInfo ? Number(this.memberInfo.has_exchanged_register_coupon) : 1
    },
    // 0 = 尚未首存；有首存後為時間戳（非 0）
    first_deposit_at() {
      return this.memberInfo ? this.memberInfo.first_deposit_at : null
    },
    // member_type=4（線下代理本人）不顯示領取彈窗
    isOfflineAgent() {
      return this.memberInfo ? Number(this.memberInfo.member_type) === 4 : false
    },
    // 上線身份為線下代理（prompt_member_type=4）時不顯示領取彈窗
    isOfflineAgentDownline() {
      return this.memberInfo ? Number(this.memberInfo.prompt_member_type) === 4 : false
    },
    // NDB（出金前）模組設定，未設定時為 null
    before_withdrawal_module_setting() {
      return this.brandInfo ? this.brandInfo.before_withdrawal_module_setting : null
    },
    shouldShow() {
      if (!this.token) return false
      if (this.dismissed) return false
      if (!this.enableRegisterCoupon) return false
      if (!this.registerCoupon) return false
      // member_type=4（線下代理本人）不論是否領過皆不顯示
      if (this.isOfflineAgent) return false
      // 上線身份為線下代理（prompt_member_type=4）不論是否領過皆不顯示
      if (this.isOfflineAgentDownline) return false
      if (this.hasExchanged !== 0) return false
      // 已首存則不顯示（即使 has_exchanged=0）；且需 NDB（before_withdrawal）模組已設定
      return this.first_deposit_at === 0 && this.before_withdrawal_module_setting !== null
    },
    amount() {
      return this.registerCoupon ? this.registerCoupon.amount : 0
    },
    formattedAmount() {
      return this.$options.filters.numberFormat(this.amount)
    },
    // 幣別符號：register_coupon.currency 後端缺少時回 null 且不推算，前端補站台幣別符號
    currencySymbol() {
      const rc = this.registerCoupon || {}
      return rc.currency || this.brand.currency || ''
    }
  },
  watch: {
    // 登出 / 換帳號時重置關閉狀態，讓下一個合格帳號可再判斷跳窗
    token(newVal) {
      if (!newVal) this.dismissed = false
    },
    registerCoupon: {
      handler(rc) {
        // 拿到新的 register_coupon（例如補完個資後重拉 /user/me）時重置關閉狀態
        this.dismissed = false
        // 有 coupon 才動態載入彈窗樣式，避免全域每頁都引入
        if (rc) this.ensureCss()
      },
      immediate: true
    }
  },
  methods: {
    ensureCss() {
      if (this.cssLoaded) return
      this.cssLoaded = true
      import('@/assets/css/register-bonus-popup.css').catch(() => {
        this.cssLoaded = false
      })
    },
    close() {
      this.dismissed = true
    },
    resolveExchangeError(code, backendMsg) {
      const abs = Math.abs(Number(code))
      if ([12, 309, 310].includes(abs)) {
        const key = 'Bao.coupon_exchange_error_' + abs
        if (this.$te(key)) return this.$t(key)
      }
      if (this.$te('Bao.coupon_exchange_error_system')) {
        return this.$t('Bao.coupon_exchange_error_system', { code })
      }
      // 該語系尚無 system 模板時退回後端訊息
      return backendMsg || ''
    },
    claim() {
      if (this.claiming) return
      const rc = this.registerCoupon
      if (!rc || !rc.code) return

      this.claiming = true

      exchangeRegisterCoupon().then(response => {
        const data = (response.data && response.data.data) || {}
        const code = Number(data.code)
        const message = data.message || ''

        // 兌換成功
        if (code === 0) {
          return info().then(res => {
            const data = res.data.data
            this.$store.dispatch('SetUserData', data)
            if (data.walletInfo) {
              this.$store.commit('SET_WALLET_INFO', data.walletInfo)
              if (this.app.selectedWallet) {
                const selectedWallet = data.walletInfo.find(
                  w => w.wallet_type === this.app.selectedWallet.wallet_type
                )
                if (selectedWallet) this.$store.commit('SET_SELECTED_WALLET', selectedWallet)
              }
            }
          })
        }

        this.dismissed = true
        // 兌換失敗
        showNotice(this.resolveExchangeError(code, message))
      }).catch(() => {
        // 傳輸層 / HTTP 錯誤：response interceptor 已顯示 content.msg，這裡不重複跳
      }).finally(() => {
        this.claiming = false
      })
    }
  }
}
</script>
